import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import authService from '../../services/auth/authService';

type UserRole = 'superadmin' | 'admin';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<UserRole>('admin');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let message: string;
      
      if (selectedRole === 'superadmin') {
        message = await authService.superAdminForgotPassword({ email });
      } else {
        message = await authService.adminForgotPassword({ email });
      }

      toast.success(message || 'Password reset link sent to your email!');
      setSubmitted(true);
      
      // Redirect to login after 5 seconds
      setTimeout(() => {
        navigate('/admin/login');
      }, 5000);

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to send reset link. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center animate-slide-up">
            <div className="bg-success-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-success-500" size={48} />
            </div>
            
            <h2 className="text-2xl font-bold text-secondary-900 mb-3">Check Your Email</h2>
            <p className="text-secondary-600 mb-6">
              We've sent a password reset link to <span className="font-semibold text-secondary-900">{email}</span>
            </p>
            
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-secondary-700">
                <strong>Note:</strong> The link will expire in 15 minutes. Please check your spam folder if you don't see the email.
              </p>
            </div>

            <Link
              to="/admin/login"
              className="inline-flex items-center justify-center space-x-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
            >
              <ArrowLeft size={20} />
              <span>Back to Login</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 flex items-center justify-center px-4 py-8">
      {/* Back to Login */}
      <Link
        to="/admin/login"
        className="fixed top-6 left-6 text-secondary-600 hover:text-primary-500 flex items-center space-x-2 transition-colors font-semibold"
      >
        <ArrowLeft size={20} />
        <span>Back to Login</span>
      </Link>

      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/rita_logo_2-removebg-preview.png" 
              alt="Rita Logo" 
              className="h-28 w-auto object-contain mx-auto"
            />
          </div>
          <div className="mb-2">
            <p className="text-sm text-secondary-600">Admin Portal</p>
          </div>
          <h2 className="text-3xl font-bold text-secondary-900 mb-2">Forgot Password?</h2>
          <p className="text-secondary-600">Enter your email to receive a password reset link</p>
        </div>

        {/* Forgot Password Form Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 animate-slide-up">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-3">
                Select Role
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedRole('superadmin')}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all ${
                    selectedRole === 'superadmin'
                      ? 'bg-primary-500 text-white border-2 border-primary-600 shadow-green'
                      : 'bg-white text-secondary-600 border-2 border-secondary-200 hover:border-primary-300'
                  }`}
                >
                  <span>Super Admin</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedRole('admin')}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all ${
                    selectedRole === 'admin'
                      ? 'bg-primary-500 text-white border-2 border-primary-600 shadow-green'
                      : 'bg-white text-secondary-600 border-2 border-secondary-200 hover:border-primary-300'
                  }`}
                >
                  <span>Admin</span>
                </button>
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                  placeholder={selectedRole === 'superadmin' ? 'superadmin@rita.com' : 'admin@rita.com'}
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <p className="text-sm text-secondary-700">
                We'll send you an email with instructions to reset your password.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3.5 rounded-lg font-semibold transition-all hover:scale-[1.02] shadow-green disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <Send size={20} />
              <span>{loading ? 'Sending...' : 'Send Reset Link'}</span>
            </button>
          </form>

          {/* Additional Help */}
          <div className="mt-6 pt-6 border-t border-secondary-200 text-center">
            <p className="text-sm text-secondary-600">
              Remember your password?{' '}
              <Link to="/admin/login" className="text-primary-600 hover:text-primary-700 font-semibold">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-secondary-500 text-sm mt-6">
          © 2025 Rita. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;

